import React from 'react';
import {
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { tokens } from '../../theme/tokens';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON_SMALL, V4_OUTLINED_DEFAULT, V4_TABLE } from '../../theme/v4Compat';
import { useNavigate } from 'react-router-dom';
import { extractApiErrorMessage, notifications } from '../../utils';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { CompareSearchService } from '../../hooks';
import { SearchCompareViewModel } from '../../models/common';
import { useConfirmation } from '../../hooks/ConfirmationService';
import useAuthContext from '../../hooks/useAuthContext';
import { HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest } from '../../api/HealthSystemApi';

interface IProps {
  farmId: string;
  searchQuery: HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest;
}

const CompareAnimalSearchModal = ({ farmId, searchQuery }: IProps) => {
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirmDialog = useConfirmation();

  const compareSearchService = new CompareSearchService();

  const [savedSearchQueries, setSavedSearchQueries] = React.useState<SearchCompareViewModel[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // Save-name dialog state
  const [nameDialogOpen, setNameDialogOpen] = React.useState(false);
  const [customName, setCustomName] = React.useState('');

  const openDialog = () => {
    setDialogOpen(true);
    loadSearchQueries();
  };

  const closeDialog = (text: string | null) => {
    if (text == null) {
      setDialogOpen(false);
      return;
    }

    const valid = validateSelected();
    if (!valid) {
      notifications.warning(t('t-compare-search-validation-error'));
      return;
    }

    navigate(`/animal/compare?ids=${selectedIds.join(',')}`);
  };

  const validateSelected = (): boolean => {
    return selectedIds.length >= 2;
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const loadSearchQueries = () => {
    setIsBusy(true);

    compareSearchService
      .getAllAsync(farmId)
      .then((result) => {
        setSavedSearchQueries(result);
        setIsBusy(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const handleRemove = React.useCallback((item: SearchCompareViewModel) => {
    confirmDialog({
      dialogId: 'delete--dialog',
      title: t('f-compare-delete-title'),
      message: t('f-compare-delete-msg', {
        name: item.name,
        timestamp: new Date(item.timestamp).toLocaleString(),
        interpolation: { escapeValue: false }
      }),
      alertSeverity: 'warning'
    }).then(() => {
      compareSearchService
        .deleteAsync(farmId, item.id)
        .then(() => {
          notifications.success(t('f-compare-delete-ok'));
          loadSearchQueries();
        })
        .catch((e: unknown) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  }, []);

  const openSaveNameDialog = () => {
    setCustomName('');
    setNameDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    const nameToUse = customName && customName.trim().length > 0 ? customName.trim() : null;
    await compareSearchService
      .saveQueryAsync(farmId, state.farmNumber, searchQuery, nameToUse)
      .then(() => {
        notifications.success(t('f-compare-save-ok'));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setNameDialogOpen(false);
      });
  };

  const handleCloseNameDialog = async () => {
    // If user closes/cancels without input -> save with auto name
    const trimmed = (customName || '').trim();
    if (trimmed.length === 0) {
      await compareSearchService
        .saveQueryAsync(farmId, state.farmNumber, searchQuery, null)
        .then(() => notifications.success(t('f-compare-save-ok')))
        .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })));
    }
    setNameDialogOpen(false);
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        color="inherit"
        sx={{ '& .MuiButton-root': V4_OUTLINED_DEFAULT }}
        aria-label={t('f-compare-search-compare')}
      >
        <Button
          id="compareCompareBtn"
          startIcon={<Icon>{'difference'}</Icon>}
          aria-haspopup="true"
          onClick={openDialog}
        >
          {t('f-compare-search-compare')}
        </Button>

        <Button id="saveCompareBtn" onClick={openSaveNameDialog} startIcon={<Icon>{'save'}</Icon>}>
          {t('f-compare-save')}
        </Button>
      </ButtonGroup>

      <Dialog
        id="compareSearchDialog"
        open={dialogOpen}
        onClose={() => closeDialog(null)}
        sx={{ overflow: 'hidden' }}
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="compareSearchDialogTitle" sx={{ background: tokens.sidebar }}>
          {t('f-compare-search-title')}
        </DialogTitle>

        <DialogContent sx={V4_DIALOG_CONTENT}>
          {isBusy ? (
            <div className="flex min-h-[150px] min-w-[240px] justify-center">
              <CircularProgress size={72} />
            </div>
          ) : (
            <Grid container direction="column" spacing={2} sx={{ minWidth: 240 }}>
              <Grid item xs={12}>
                <Table size="small" sx={V4_TABLE} id="animalSearchResultTable">
                  <TableBody>
                    {savedSearchQueries.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(item.id.toString())}
                            onChange={() => handleCheckboxChange(item.id.toString())}
                          />
                        </TableCell>
                        <TableCell># {item.name}</TableCell>
                        <TableCell>{new Date(item.timestamp).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <Grid container direction="row">
                            <Tooltip arrow title={t('f-com-remove')}>
                              <IconButton
                                color="primary"
                                size="small"
                                sx={V4_ICON_BUTTON_SMALL}
                                onClick={() => handleRemove(item)}
                              >
                                <Icon>{'delete_outline'}</Icon>
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ background: tokens.sidebar }}>
          <Button
            onClick={() => closeDialog(null)}
            startIcon={<Icon>{'close'}</Icon>}
            disabled={isBusy}
            variant="outlined"
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>

          <Button
            onClick={() => closeDialog('OK')}
            startIcon={<Icon>{'difference'}</Icon>}
            variant="contained"
            color="primary"
          >
            {t('f-compare-search-compare')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save name dialog */}
      <Dialog
        id="saveCompareNameDialog"
        open={nameDialogOpen}
        onClose={async () => {
          await handleCloseNameDialog();
        }}
        maxWidth="sm"
      >
        <DialogTitle sx={{ background: tokens.sidebar }}>{t('f-compare-save')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <Grid container spacing={2}>
            <Grid item>
              <Alert severity="warning" style={{ marginBottom: '16px' }}>
                <Typography variant="body2">{t('f-compare-warning-note')}</Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t('f-compare-save-query-name')}
                variant="outlined"
                size="small"
                fullWidth
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: tokens.sidebar }}>
          <Button onClick={handleCloseNameDialog} startIcon={<Icon>{'close'}</Icon>} color="primary" variant="outlined">
            {t('m-global-cancel')}
          </Button>
          <Button onClick={handleConfirmSave} startIcon={<Icon>{'check'}</Icon>} color="primary" variant="contained">
            {t('f-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompareAnimalSearchModal;
